class HeartsNotesQuiz extends HTMLElement {
  constructor() {
    super();
    this.definition = {};
    this.meta = {};
    this.products = { primary: null, linked: [] };
    this.questions = [];
    this.transitions = [];
    this.phase = 'welcome';
    this.currentIndex = 0;
    this.selected = null;
    this.answers = [];
    this.firstName = '';
    this.email = '';
    this.error = '';
    this.isSubmitting = false;
    this.assessmentStep = 0;
    this.assessingTarget = 'score';
    this.noteRevealed = false;
    this.noteJustRevealed = false;
    this.audioContext = null;
    this.audioUnlocked = false;
    this.soundOn = true;
    this.handleClick = this.handleClick.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.unlockAudio = this.unlockAudio.bind(this);
  }

  connectedCallback() {
    this.definition = this.readJson(this.dataset.definition) || {};
    this.meta = this.readJson(this.dataset.meta) || {};
    this.products = this.readJson(this.dataset.products) || { primary: null, linked: [] };

    if (typeof this.definition === 'string') {
      try {
        this.definition = JSON.parse(this.definition);
      } catch (error) {
        this.definition = {};
      }
    }

    this.questions = this.flattenQuestions(this.definition.blocks || []);
    this.transitions = Array.isArray(this.definition.transitions) ? this.definition.transitions : [];
    this.addEventListener('click', this.handleClick);
    this.addEventListener('submit', this.handleSubmit);
    window.addEventListener('pointerdown', this.unlockAudio, { once: true, passive: true });
    window.addEventListener('touchstart', this.unlockAudio, { once: true, passive: true });
    this.render();
    window.requestAnimationFrame(() => {
      this.classList.remove('hnq-loading');
      this.classList.add('hnq-ready');
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('submit', this.handleSubmit);
  }

  getStoredSoundPreference() {
    try {
      const stored = window.localStorage.getItem('hn_quiz_sound_on');
      return stored === null ? true : stored === 'true';
    } catch (error) {
      return true;
    }
  }

  setStoredSoundPreference(value) {
    try {
      window.localStorage.setItem('hn_quiz_sound_on', String(value));
    } catch (error) {}
  }

  readJson(selector) {
    if (!selector) return null;
    const node = document.querySelector(selector);
    if (!node) return null;

    try {
      return JSON.parse(node.textContent.trim());
    } catch (error) {
      return null;
    }
  }

  flattenQuestions(blocks) {
    const result = [];

    blocks.forEach((block) => {
      (block.questions || []).forEach((question) => {
        result.push({
          ...question,
          blockId: block.id,
          blockLabel: block.label || ''
        });
      });
    });

    return result;
  }

  get total() {
    return this.questions.length;
  }

  get score() {
    return this.questions.reduce((sum, question, index) => {
      return sum + (this.answers[index] === question.correct ? 1 : 0);
    }, 0);
  }

  get scoreBand() {
    const score = this.score;
    const bands = Array.isArray(this.definition.scoreBands) ? this.definition.scoreBands : [];
    return bands.find((band) => score >= Number(band.min) && score <= Number(band.max)) || {
      title: 'Your result',
      message: 'Thank you for taking this Hearts Notes quiz.'
    };
  }

  get currentQuestion() {
    return this.questions[this.currentIndex];
  }

  get matchedLetter() {
    const score = this.score;
    const letters = Array.isArray(this.definition.letters) ? this.definition.letters : [];
    return letters.find((letter) => score >= Number(letter.min) && score <= Number(letter.max));
  }

  get matchedAnalysis() {
    const score = this.score;
    const items = Array.isArray(this.definition.analysis) ? this.definition.analysis : [];
    return items.find((item) => score >= Number(item.min) && score <= Number(item.max));
  }

  get matchedRecommendation() {
    const score = this.score;
    const items = Array.isArray(this.definition.recommendationBands) ? this.definition.recommendationBands : [];
    return items.find((item) => score >= Number(item.min) && score <= Number(item.max));
  }

  get recommendationSettings() {
    return this.definition.recommendation || this.definition.promo || {};
  }

  get displayName() {
    return this.firstName ? this.firstName : '';
  }

  greet(text) {
    return this.displayName ? text.replace('{firstName}', this.displayName) : text.replace('{firstName}, ', '').replace('{firstName}', '');
  }

  templateText(text, fallback = '') {
    const value = String(text || fallback || '');
    const firstName = this.displayName || '';
    return value
      .replace(/\{firstName\}/g, firstName)
      .replace(/\{name\}/g, firstName)
      .replace(/\s+,/g, ',')
      .replace(/^,\s*/, '')
      .trim();
  }

  get assessmentLines() {
    const custom = this.definition.assessmentLines;
    if (Array.isArray(custom) && custom.length) return custom;

    const namePrefix = this.displayName ? `${this.displayName}, ` : '';
    return [
      `${namePrefix}reading your answers`,
      'Checking your quiz pattern',
      'Preparing your score',
      'Building your answer report',
      'Finding the Hearts Notes material connected to this result'
    ];
  }

  escape(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  unlockAudio() {
    if (!this.soundOn) return;
    const ctx = this.ensureAudioContext(false);
    if (!ctx) return;
    ctx.resume?.().then(() => {
      this.audioUnlocked = true;
    }).catch(() => {});
  }

  ensureAudioContext(playable = true) {
    if (!this.soundOn) return null;
    if (!window.AudioContext && !window.webkitAudioContext) return null;

    if (!this.audioContext) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      this.audioContext = new AudioContextClass();
    }

    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume().catch(() => {});
    }

    if (playable) this.audioUnlocked = true;
    return this.audioContext;
  }

  vibrate(pattern) {
    if ('vibrate' in navigator) navigator.vibrate(pattern);
  }

  playTone(frequency, duration = 0.08, type = 'sine', delay = 0, gainValue = 0.05) {
    const ctx = this.ensureAudioContext(true);
    if (!ctx) return;

    if (ctx.state === 'suspended') {
      ctx.resume().then(() => this.playTone(frequency, duration, type, delay, gainValue)).catch(() => {});
      return;
    }

    const start = ctx.currentTime + delay;
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();

    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, start);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(gainValue, start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);

    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.04);
  }

  playSound(name) {
    if (!this.soundOn) return;

    if (name === 'select') {
      this.playTone(660, 0.10, 'triangle', 0, 0.055);
      return;
    }

    if (name === 'transition') {
      this.playTone(523.25, 0.18, 'sine', 0, 0.04);
      this.playTone(659.25, 0.18, 'sine', 0.02, 0.036);
      this.playTone(783.99, 0.22, 'sine', 0.04, 0.032);
      return;
    }

    if (name === 'bell') {
      this.playTone(987.77, 0.22, 'triangle', 0, 0.055);
      this.playTone(1318.51, 0.32, 'sine', 0.05, 0.036);
      this.playTone(1760.00, 0.26, 'sine', 0.10, 0.024);
      return;
    }

    this.playTone(880, 0.07, 'sine', 0, 0.048);
  }

  updateProgress() {
    const showProgress = this.phase === 'question' || this.phase === 'transition';
    const width = showProgress && this.total > 0 ? Math.round((this.currentIndex / this.total) * 100) : 0;

    const internalTargets = this.querySelectorAll('[data-hnq-card-progress]');
    internalTargets.forEach((target) => {
      target.style.width = `${width}%`;
    });

    const externalTarget = this.dataset.progressTarget ? document.querySelector(this.dataset.progressTarget) : null;
    if (externalTarget) externalTarget.style.width = `${width}%`;
  }

  scrollToQuizCard() {
    const shell = this.querySelector('.hnq-shell') || this.querySelector('.hnq-card') || this;
    if (!shell || !shell.getBoundingClientRect) return;

    window.requestAnimationFrame(() => {
      const rect = shell.getBoundingClientRect();
      const currentY = window.pageYOffset || document.documentElement.scrollTop || 0;
      const offset = window.innerWidth < 750 ? 14 : 24;
      const targetY = Math.max(0, currentY + rect.top - offset);

      window.scrollTo({
        top: targetY,
        behavior: 'smooth'
      });
    });
  }

  renderAndScroll() {
    this.render();
    this.scrollToQuizCard();
  }

  setPhase(phase) {
    this.phase = phase;
    this.error = '';
    this.playSound('tap');
    this.renderAndScroll();
  }

  toggleSound() {
    this.soundOn = !this.soundOn;
    this.setStoredSoundPreference(this.soundOn);
    if (this.soundOn) {
      this.unlockAudio();
      this.playSound('tap');
    }
    this.render();
  }

  startQuiz() {
    this.unlockAudio();
    this.phase = 'question';
    this.currentIndex = 0;
    this.selected = this.answers[0] ?? null;
    this.playSound('tap');
    this.vibrate(8);
    this.renderAndScroll();
  }

  selectOption(index) {
    this.selected = index;
    this.playSound('select');
    this.vibrate(12);
    this.render();
  }

  previousQuestion() {
    this.playSound('tap');
    this.vibrate(8);

    if (this.phase === 'question' && this.currentIndex === 0) {
      this.phase = 'welcome';
      this.renderAndScroll();
      return;
    }

    if (this.phase === 'question') {
      this.currentIndex -= 1;
      this.selected = this.answers[this.currentIndex] ?? null;
      this.renderAndScroll();
      return;
    }

    if (this.phase === 'capture') {
      this.phase = 'question';
      this.currentIndex = Math.max(0, this.total - 1);
      this.selected = this.answers[this.currentIndex] ?? null;
      this.renderAndScroll();
    }
  }

  nextQuestion() {
    if (this.selected === null || this.selected === undefined) return;

    this.answers[this.currentIndex] = this.selected;
    this.playSound('tap');
    this.vibrate(8);

    const completedQuestion = this.currentIndex + 1;
    const transition = this.transitions.find((item) => Number(item.afterQuestion) === completedQuestion);

    if (transition) {
      this.phase = 'transition';
      this.playSound('transition');
      this.vibrate([10, 40, 10]);
      this.renderAndScroll();
      return;
    }

    this.advanceAfterQuestion();
  }

  advanceAfterQuestion() {
    if (this.currentIndex + 1 >= this.total) {
      this.selected = null;
      this.startAssessment('capture');
      return;
    }

    this.currentIndex += 1;
    this.selected = this.answers[this.currentIndex] ?? null;
    this.phase = 'question';
    this.renderAndScroll();
  }

  continueAfterTransition() {
    this.playSound('tap');
    this.vibrate(8);
    this.advanceAfterQuestion();
  }

  async startAssessment(target = 'score') {
    this.assessingTarget = target;
    this.error = '';
    this.assessmentStep = 0;
    this.isSubmitting = false;
    this.phase = 'assessing';
    this.playSound('transition');
    this.vibrate([10, 40, 10]);
    this.renderAndScroll();

    await this.runAssessmentAnimation();

    this.phase = target;
    this.assessmentStep = 0;
    if (target === 'capture') {
      this.playSound('bell');
      this.vibrate([15, 30, 15]);
    }
    this.renderAndScroll();
  }

  submitLead(event) {
    event.preventDefault();
    this.submitLeadFromForm(event.target);
  }

  async submitLeadFromForm(form) {
    if (!form) return;

    const firstName = String(new FormData(form).get('firstName') || '').trim();
    const email = String(new FormData(form).get('email') || '').trim();

    if (!firstName || !email || !email.includes('@')) {
      this.error = 'Enter your first name and a valid email to reveal your result.';
      this.render();
      return;
    }

    this.firstName = firstName;
    this.email = email;
    this.error = '';
    this.isSubmitting = true;
    this.playSound('tap');
    this.vibrate(8);
    this.render();

    await this.postWebhook(this.buildPayload());

    this.isSubmitting = false;
    this.phase = 'score';
    this.playSound('bell');
    this.vibrate([15, 30, 15]);
    this.renderAndScroll();
  }

  async runAssessmentAnimation() {
    const lines = this.assessmentLines;
    const stepDelay = Math.max(750, Math.min(1200, Number(this.dataset.assessmentDelay || 900)));

    for (let index = 0; index < lines.length; index += 1) {
      this.assessmentStep = index;
      this.render();
      this.playSound(index === lines.length - 1 ? 'transition' : 'tap');
      await new Promise((resolve) => window.setTimeout(resolve, stepDelay));
    }

    await new Promise((resolve) => window.setTimeout(resolve, 700));
  }

  sanitizeTag(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/_{2,}/g, '_');
  }

  absoluteUrl(url) {
    if (!url) return '';
    try {
      return new URL(url, window.location.origin).toString();
    } catch (error) {
      return String(url || '');
    }
  }

  buildPreviewUrl(product, campaign) {
    if (!product?.id) return '';

    const url = new URL('/pages/free-preview', window.location.origin);
    url.searchParams.set('pid', product.id);
    url.searchParams.set('utm_source', 'heartsnotes');
    url.searchParams.set('utm_medium', 'quiz');
    url.searchParams.set('utm_campaign', campaign || 'quiz');
    return url.toString();
  }

  productPayload(product, campaign) {
    if (!product) return null;

    return {
      id: product.id || null,
      handle: product.handle || '',
      title: product.title || '',
      short_name: product.customShortNameText || product.shortTitleText || product.shortTitle || product.title || '',
      url: this.absoluteUrl(product.url || ''),
      preview_url: this.buildPreviewUrl(product, campaign)
    };
  }

  buildPayload() {
    const band = this.scoreBand;
    const analysis = this.matchedAnalysis || {};
    const personalNote = this.matchedLetter || {};
    const recommendationCopy = this.getRecommendationCopy();
    const quizSlug = this.definition.slug || this.meta.slug || '';
    const quizTitle = this.definition.meta?.title || this.meta.title || document.title;
    const categories = Array.isArray(this.meta.categories) ? this.meta.categories : [];
    const fallbackCategory = categories.find((category) => category && (category.slug || category.name)) || {};
    const primaryCategorySlug = this.meta.primaryCategorySlug || this.meta.category || fallbackCategory.slug || '';
    const primaryCategoryLabel = this.meta.primaryCategoryLabel || this.meta.categoryLabel || fallbackCategory.name || '';
    const campaign = primaryCategorySlug || 'quiz';
    const submittedAt = new Date().toISOString();
    const pageUrl = this.absoluteUrl(this.meta.pageUrl || window.location.href);
    const primaryProduct = this.products?.primary || null;
    const linkedProducts = Array.isArray(this.products?.linked) ? this.products.linked : [];

    return {
      source: 'shopify_quiz',
      store: window.location.hostname,
      lead_intent: 'quiz',
      first_name: this.firstName,
      email: this.email,
      quiz: {
        slug: quizSlug,
        title: quizTitle,
        primary_category: {
          name: primaryCategoryLabel,
          slug: primaryCategorySlug
        },
        categories: categories.map((category) => ({
          name: category?.name || '',
          slug: category?.slug || ''
        }))
      },
      result: {
        score: this.score,
        total: this.total,
        score_percentage: this.total > 0 ? Math.round((this.score / this.total) * 100) : 0,
        score_band: {
          title: band.title || '',
          message: band.message || ''
        },
        analysis: {
          headline: analysis.headline || '',
          body: analysis.body || ''
        },
        note_for_you: {
          title: 'A note for you',
          body: this.templateText(personalNote.body || '')
        },
        recommendation: {
          headline: recommendationCopy.headline || '',
          body: recommendationCopy.body || '',
          primary_product: this.productPayload(primaryProduct, campaign),
          linked_products: linkedProducts.map((product) => this.productPayload(product, campaign)).filter(Boolean)
        }
      },
      submitted_at: submittedAt,
      page_url: pageUrl,
      referrer: document.referrer || '',
      utm_source: 'heartsnotes',
      utm_medium: 'quiz',
      utm_campaign: campaign
    };
  }

  async postWebhook(payload) {
    const url = this.dataset.webhookUrl;
    if (!url) return;

    try {
      await fetch(url, {
        method: 'POST',
        mode: 'no-cors',
        keepalive: true,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify(payload)
      });
    } catch (error) {
      console.warn('HN quiz webhook failed', error);
    }
  }

  productList() {
    const primary = this.products?.primary ? [this.products.primary] : [];
    const linked = Array.isArray(this.products?.linked) ? this.products.linked : [];
    const seen = new Set();

    return [...primary, ...linked].filter((product) => {
      const key = product?.id || product?.handle || product?.url;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  linkedProductList() {
    const primaryKey = this.products?.primary?.id || this.products?.primary?.handle || this.products?.primary?.url;
    return this.productList().filter((product) => {
      const key = product?.id || product?.handle || product?.url;
      return key !== primaryKey;
    });
  }

  handleClick(event) {
    const button = event.target.closest('[data-action]');
    if (!button || !this.contains(button)) return;

    const action = button.dataset.action;

    if (action === 'start') this.startQuiz();
    if (action === 'select') this.selectOption(Number(button.dataset.index));
    if (action === 'next') this.nextQuestion();
    if (action === 'previous') this.previousQuestion();
    if (action === 'continue') this.continueAfterTransition();
    if (action === 'capture') this.submitLeadFromForm(button.closest('form') || this.querySelector('[data-hn-capture-form]'));
    if (action === 'analysis') this.setPhase('reveal');
    if (action === 'reveal') this.setPhase('reveal');
    if (action === 'score') this.setPhase('score');
    if (action === 'toggle-note') { this.noteRevealed = true; this.noteJustRevealed = true; this.playSound('transition'); this.vibrate([10, 30, 10]); this.render(); window.setTimeout(() => { this.noteJustRevealed = false; }, 900); }
    if (action === 'copy-share') this.copyShareLink(button);
  }

  handleSubmit(event) {
    if (event.target.matches('[data-hn-capture-form]')) {
      this.submitLead(event);
    }
  }

  render() {
    if (!this.total) {
      this.innerHTML = `${this.renderShell(this.renderMissing())}${this.renderShareQuiz()}`;
      this.updateProgress();
      return;
    }

    const screens = {
      welcome: () => this.renderWelcome(),
      question: () => this.renderQuestion(),
      transition: () => this.renderTransition(),
      capture: () => this.renderCapture(),
      assessing: () => this.renderAssessing(),
      score: () => this.renderScore(),
      reveal: () => this.renderReveal()
    };

    this.innerHTML = `${this.renderShell(screens[this.phase]?.() || screens.welcome())}${this.renderShareQuiz()}`;
    this.updateProgress();
  }

  renderShell(content) {
    const showProgress = this.phase === 'question' || this.phase === 'transition';
    const progressWidth = showProgress && this.total > 0 ? Math.round((this.currentIndex / this.total) * 100) : 0;
    const title = this.definition.meta?.title || this.meta.title || 'Hearts Notes Quiz';

    return `
      <div class="hnq-shell">
        <div class="hnq-shell-top" aria-label="Quiz title">
          <span class="hnq-shell-title">Quiz: ${this.escape(title)}</span>
        </div>
        <div class="hnq-progress ${showProgress ? 'is-visible' : ''}" aria-hidden="true">
          <span data-hnq-card-progress style="width: ${progressWidth}%;"></span>
        </div>
        ${content}
        <div class="hnq-shell-bottom">
          ${this.renderBackToQuizzes('hnq-back-link--shell-bottom')}
        </div>
      </div>
    `;
  }

  renderPoweredBy() {
    const logo = this.meta.siteLogo;
    const logoMarkup = logo
      ? `<img class="hnq-powered__logo" src="${this.escape(logo)}" alt="Hearts Notes">`
      : '<span class="hnq-powered__logo-text">Hearts Notes</span>';

    return `
      <div class="hnq-powered-wrap" aria-label="Powered by Hearts Notes">
        <span class="hnq-powered">Powered by ${logoMarkup}</span>
      </div>
    `;
  }

  shareUrl() {
    const canonical = this.meta.pageUrl || window.location.href;
    try {
      const url = new URL(canonical, window.location.origin);
      url.search = '';
      url.hash = '';
      return url.toString();
    } catch (error) {
      return window.location.href.split('#')[0].split('?')[0];
    }
  }

  shareText() {
    const title = this.definition.meta?.title || this.meta.title || 'Hearts Notes Quiz';
    return `${title} | Hearts Notes`;
  }

  renderShareQuiz() {
    const url = this.shareUrl();
    const encodedUrl = encodeURIComponent(url);
    const encodedText = encodeURIComponent(this.shareText());
    const whatsappText = encodeURIComponent(`${this.shareText()} ${url}`);

    return `
      <nav class="hnq-share-row" aria-label="Share this quiz">
        <span class="hnq-share-row__label">Share</span>
        <button class="hnq-share-row__button" type="button" data-action="copy-share" data-share-url="${this.escape(url)}" aria-label="Copy quiz link" title="Copy link">
          <span class="hnq-share-row__icon" aria-hidden="true">↗</span>
          <span class="hnq-share-row__text" data-copy-label>Copy</span>
        </button>
        <a class="hnq-share-row__button" href="https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}" target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" title="Facebook">
          <span class="hnq-share-row__icon hnq-share-row__icon--facebook" aria-hidden="true">f</span>
          <span class="hnq-share-row__text">Facebook</span>
        </a>
        <a class="hnq-share-row__button" href="https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}" target="_blank" rel="noopener noreferrer" aria-label="Share on X" title="X">
          <span class="hnq-share-row__icon" aria-hidden="true">𝕏</span>
          <span class="hnq-share-row__text">X</span>
        </a>
        <a class="hnq-share-row__button" href="https://api.whatsapp.com/send?text=${whatsappText}" target="_blank" rel="noopener noreferrer" aria-label="Share on WhatsApp" title="WhatsApp">
          <span class="hnq-share-row__icon" aria-hidden="true">☏</span>
          <span class="hnq-share-row__text">WhatsApp</span>
        </a>
      </nav>
    `;
  }

  copyShareLink(button) {
    const url = button?.dataset?.shareUrl || this.shareUrl();
    const label = button?.querySelector('[data-copy-label]');
    const original = label ? label.textContent : '';
    const done = () => {
      if (label) {
        label.textContent = 'Copied';
        window.setTimeout(() => { label.textContent = original || 'Copy link'; }, 1700);
      }
      this.playSound('select');
      this.vibrate(12);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(() => {
        window.prompt('Copy this quiz link:', url);
      });
      return;
    }

    window.prompt('Copy this quiz link:', url);
  }

  renderMissing() {
    return `
      <div class="hnq hnq-card hnq-empty">
        <p class="hnq-eyebrow">Quiz setup required</p>
        <h1 class="hnq-title">Questions are missing</h1>
        <p class="hnq-subtitle">Add valid quiz questions inside the Quiz Definition JSON.</p>
      </div>
    `;
  }

  renderBackToQuizzes(className = '') {
    const href = this.meta.quizIndexUrl || '/pages/quiz';
    return `<a class="hnq-back-link ${className}" href="${this.escape(href)}">← Back to all quizzes</a>`;
  }

  renderSoundToggle() {
    return `
      <button class="hnq-sound" type="button" data-action="sound" aria-label="${this.soundOn ? 'Turn sound off' : 'Turn sound on'}">
        <span aria-hidden="true">${this.soundOn ? '♪' : '×'}</span>
        <span>${this.soundOn ? 'Sound on' : 'Sound off'}</span>
      </button>
    `;
  }

  renderChallengeRibbon() {
    const params = new URLSearchParams(window.location.search);
    const from = params.get('from');
    const score = params.get('score');
    if (!from || !score) return '';

    return `<p class="hnq-challenge">${this.escape(from)} scored ${this.escape(score)}/${this.total}. Can you beat them?</p>`;
  }


  formatPlayedCountLabel(value) {
    const raw = value === null || value === undefined ? '' : String(value).trim();
    if (!raw) return '';
    if (/^[\d\s,.]+$/.test(raw)) {
      return `${raw} played`;
    }
    return raw;
  }

  renderCategoryPills() {
    const categories = Array.isArray(this.meta.categories) ? this.meta.categories : [];
    const primarySlug = this.meta.primaryCategorySlug || this.meta.category || '';
    return categories
      .filter((category) => category && category.slug && category.slug !== primarySlug)
      .slice(0, 3)
      .map((category) => `<span class="hnq-pill hnq-pill--tag">${this.escape(category.name || category.slug)}</span>`)
      .join('');
  }

  renderWelcome() {
    const meta = this.definition.meta || {};
    const image = this.meta.image;

    return `
      <div class="hnq hnq-card hnq-welcome">
        <div class="hnq-card__inner">
          <div class="hnq-media-stack">
            <div class="hnq-media">
              ${image ? `<img src="${this.escape(image)}" alt="${this.escape(this.meta.imageAlt || this.meta.title || meta.title || 'Quiz image')}">` : '<div class="hnq-media__placeholder"></div>'}
              <span class="hnq-floating-icon">${this.escape(this.meta.icon || '✦')}</span>
            </div>
            ${meta.welcomeStat ? `<div class="hnq-didyouknow hnq-didyouknow--media"><strong>Did you know?</strong><span>${this.escape(meta.welcomeStat)}</span></div>` : ''}
          </div>
          <div class="hnq-welcome__content">
            ${this.renderChallengeRibbon()}
            <p class="hnq-eyebrow">${this.escape(meta.preHeadline || this.meta.primaryCategoryLabel || this.meta.categoryLabel || 'Faith-filled quiz')}</p>
            <h1 class="hnq-title">${this.escape(meta.title || this.meta.title || 'Hearts Notes Quiz')}</h1>
            <p class="hnq-subtitle">${this.escape(meta.subtitle || this.meta.description || '')}</p>
            ${this.meta.seoIntro ? `<p class="hnq-seo-intro">${this.escape(this.meta.seoIntro)}</p>` : ''}
            <div class="hnq-meta-row">
              <span class="hnq-pill hnq-pill--primary">${this.escape(this.meta.primaryCategoryLabel || this.meta.categoryLabel || 'Quiz')}</span>
              ${this.renderCategoryPills()}
              <span class="hnq-pill">${this.escape(this.meta.timeLabel || '3 min')}</span>
              ${this.formatPlayedCountLabel(this.meta.playedCountLabel) ? `<span class="hnq-pill">${this.escape(this.formatPlayedCountLabel(this.meta.playedCountLabel))}</span>` : ''}
              ${this.meta.badge ? `<span class="hnq-pill hnq-pill--warm">${this.escape(this.meta.badge)}</span>` : ''}
            </div>
            <div class="hnq-actions hnq-actions--start">
              <button class="hnq-button" type="button" data-action="start">${this.escape(meta.ctaStart || 'Begin the quiz')}</button>
            </div>
            ${meta.ctaUnderStart ? `<p class="hnq-under-note">${this.escape(meta.ctaUnderStart)}</p>` : ''}
          </div>
        </div>
      </div>
    `;
  }

  renderQuestion() {
    const question = this.currentQuestion;
    const count = this.currentIndex + 1;
    const letters = ['A', 'B', 'C', 'D', 'E', 'F'];
    const didYouKnow = Array.isArray(this.definition.didYouKnow) && count % 3 === 0
      ? this.definition.didYouKnow[(count - 1) % this.definition.didYouKnow.length]
      : '';

    return `
      <div class="hnq hnq-card hnq-question-wrap">
        <div class="hnq-question-card">
          <div class="hnq-question-top">
            ${question.blockLabel ? `<span class="hnq-question-topic"><span class="hnq-question-topic__label">Topic:</span> ${this.escape(question.blockLabel)}</span>` : '<span></span>'}
            <span class="hnq-question-count hnq-question-count--question"><span class="hnq-count-long">Question ${count} of ${this.total}</span><span class="hnq-count-short">${count}/${this.total}</span></span>
          </div>
          <h2 class="hnq-question-title">${this.escape(question.q)}</h2>
          <div class="hnq-options">
            ${(question.options || []).map((option, index) => `
              <button class="hnq-option ${this.selected === index ? 'is-selected' : ''}" type="button" data-action="select" data-index="${index}" aria-pressed="${this.selected === index ? 'true' : 'false'}">
                <span class="hnq-option__letter">${letters[index] || index + 1}</span>
                <span class="hnq-option__text">${this.escape(option)}</span>
                <span class="hnq-option__check" aria-hidden="true">✓</span>
              </button>
            `).join('')}
          </div>
          ${didYouKnow ? `<div class="hnq-didyouknow hnq-didyouknow--small"><strong>Did you know?</strong><span>${this.escape(didYouKnow)}</span></div>` : ''}
          <div class="hnq-question-actions">
            <button class="hnq-text-button" type="button" data-action="previous">${this.currentIndex === 0 ? 'Back to intro' : 'Back'}</button>
            <button class="hnq-button" type="button" data-action="next" ${this.selected === null || this.selected === undefined ? 'disabled' : ''}>Next question</button>
          </div>
        </div>
      </div>
    `;
  }

  renderTransition() {
    const completedQuestion = this.currentIndex + 1;
    const transition = this.transitions.find((item) => Number(item.afterQuestion) === completedQuestion) || {};

    return `
      <div class="hnq hnq-card hnq-transition">
        <div class="hnq-transition__orb">${this.escape(transition.emoji || '✦')}</div>
        <p class="hnq-question-count">Checkpoint · ${completedQuestion} of ${this.total}</p>
        <h2>${this.escape(transition.headline || 'Keep going')}</h2>
        <p>${this.escape(transition.body || transition.remaining || '')}</p>
        <div class="hnq-actions hnq-actions--center">
          <button class="hnq-button" type="button" data-action="continue">${this.escape(transition.cta || 'Keep going')}</button>
        </div>
      </div>
    `;
  }

  renderCapture() {
    const capture = this.definition.capture || {};
    const captureHeadline = capture.headline && !/almost\s+there/i.test(String(capture.headline))
      ? capture.headline
      : 'Your result is ready';
    const captureSubtitle = capture.subtitle && !/^enter your name and email to reveal your result on this page\.?$/i.test(String(capture.subtitle).trim())
      ? capture.subtitle
      : 'Enter your name and email to reveal your score, analysis, corrected answers, and recommended Hearts Notes material.';
    const captureCta = capture.cta && !/see my (score|result)/i.test(String(capture.cta))
      ? capture.cta
      : 'Reveal my result';

    return `
      <div class="hnq hnq-card hnq-capture">
        <button class="hnq-text-button hnq-text-button--top" type="button" data-action="previous">Back to last question</button>
        <h2>${this.escape(captureHeadline)}</h2>
        <p class="hnq-capture__intro">${this.escape(captureSubtitle)}</p>
        <form class="hnq-form hnq-form--capture" data-hn-capture-form novalidate>
          <input class="hnq-input" name="firstName" type="text" autocomplete="given-name" placeholder="First name" value="${this.escape(this.firstName)}" required>
          <input class="hnq-input" name="email" type="email" autocomplete="email" placeholder="Email address" value="${this.escape(this.email)}" required>
          <button class="hnq-button" type="button" data-action="capture" ${this.isSubmitting ? 'disabled' : ''}>${this.escape(captureCta)}</button>
        </form>
        <p class="hnq-privacy">${this.escape(capture.privacyNote || 'Your result will appear here after submission.')}</p>
        ${this.error ? `<p class="hnq-error">${this.escape(this.error)}</p>` : ''}
      </div>
    `;
  }

  renderAssessing() {
    const lines = this.assessmentLines;
    const headline = 'Analyzing your answers';
    const subline = 'Give us a few seconds. We are preparing your score, your analysis, and the Hearts Notes material connected to this result.';

    return `
      <div class="hnq hnq-card hnq-assessing" aria-live="polite">
        <div class="hnq-assessing__orb"><span></span></div>
        <p class="hnq-eyebrow">Analyzing your answers</p>
        <h2>${this.escape(headline)}</h2>
        <p class="hnq-assessing__subtitle">${this.escape(subline)}</p>
        <div class="hnq-assessing__lines">
          ${lines.map((line, index) => `
            <div class="hnq-assessing__line ${index <= this.assessmentStep ? 'is-visible' : ''}">
              <span></span>${this.escape(line)}
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  renderScore() {
    const band = this.scoreBand;
    const message = band.message || 'Your score is ready. Open your analysis to review what your answers show.';
    const name = this.displayName;
    const scoreTitle = name ? `${name}, your score is ready` : 'Your score is ready';

    return `
      <div class="hnq hnq-card hnq-score hnq-score-v9">
        <div class="hnq-score__burst" aria-hidden="true"></div>
        <p class="hnq-eyebrow">Your quiz result</p>
        <h2>${this.escape(scoreTitle)}</h2>
        <div class="hnq-score__number" aria-label="Score ${this.score} out of ${this.total}">${this.score}<span>/${this.total}</span></div>
        <p class="hnq-score__band"><strong>${this.escape(band.title || 'Your result')}</strong></p>
        <p class="hnq-score__message">${this.escape(name ? `Hi ${name}, ${message}` : message)}</p>
        <div class="hnq-score-cta-panel hnq-score-cta-panel--v9">
          <div class="hnq-score-cta-panel__icon" aria-hidden="true">✦</div>
          <div>
            <p class="hnq-score-cta-panel__label">Your full answer report is ready behind this step.</p>
            <p>Open your analysis to see your corrected answers and the Hearts Notes material connected to this result.</p>
          </div>
          <button class="hnq-button hnq-button--analysis" type="button" data-action="analysis">See my analysis and corrected answers</button>
        </div>
      </div>
    `;
  }

  getRecommendationCopy() {
    const settings = this.recommendationSettings;
    const matched = this.matchedRecommendation || {};
    const defaultHeadline = this.displayName
      ? `${this.displayName}, this is the material we recommend for your result`
      : 'This is the material we recommend for your result';

    return {
      eyebrow: matched.eyebrow || settings.eyebrow || settings.offerLabel || 'Recommended for you',
      headline: this.templateText(matched.headlineTemplate || matched.headline || settings.headlineTemplate || settings.materialHeadlineTemplate || settings.offerHeadline, defaultHeadline),
      body: this.templateText(matched.body || matched.reason || settings.body || settings.materialIntroText || settings.subtitle, 'We created these materials to help turn reflection into a simple next step: something you can read, keep, print, and return to when you need structure and encouragement.'),
      primaryLinkLabel: matched.primaryLinkLabel || settings.primaryLinkLabel || settings.linkLabel || 'Discover this material',
      linkedLinkLabel: matched.linkedLinkLabel || settings.linkedLinkLabel || 'See more'
    };
  }

  renderAnalysisInsight() {
    const analysis = this.matchedAnalysis;
    if (!analysis || (!analysis.headline && !analysis.body)) return '';

    return `
      <div class="hnq-analysis-insight">
        <div class="hnq-analysis-insight__icon" aria-hidden="true">✦</div>
        <div>
          ${analysis.headline ? `<h3>${this.escape(this.templateText(analysis.headline))}</h3>` : ''}
          ${analysis.body ? `<p>${this.escape(this.templateText(analysis.body))}</p>` : ''}
        </div>
      </div>
    `;
  }

  renderReveal() {
    const letter = this.matchedLetter;
    const showProducts = this.dataset.showProducts === 'true' && this.productList().length > 0;
    const name = this.displayName;
    const greeting = name ? `${name}, thank you for the time you dedicated to this quiz.` : 'Thank you for the time you dedicated to this quiz.';

    return `
      <div class="hnq hnq-card hnq-reveal hnq-reveal-v9">
        <div class="hnq-full-report-intro">
          <div class="hnq-reveal__icon" aria-hidden="true">✦</div>
          <p class="hnq-eyebrow">Your full report</p>
          <h2>${this.escape(greeting)}</h2>
          <p>We appreciate the attention you gave to this matter. We analyzed your answers and prepared your full report, including your score, your answer review, and the Hearts Notes materials that may help you keep learning with more clarity and structure.</p>
        </div>

        <section class="hnq-analysis-report-block">
          <p class="hnq-eyebrow">Your Analysis Report</p>
          ${this.renderAnalysisInsight()}
          ${letter?.body ? this.renderPersonalNote(letter.body) : ''}
        </section>

        <section class="hnq-corrected-answers">
          <div class="hnq-section-heading">
            <p class="hnq-eyebrow">Corrected answers</p>
            <h3>Review your answers</h3>
          </div>
          <div class="hnq-answer-list">
            ${this.questions.map((question, index) => this.renderAnswer(question, index)).join('')}
          </div>
        </section>

        ${showProducts ? this.renderProducts() : ''}
      </div>
    `;
  }


  renderPersonalNote(body) {
    const revealed = this.noteRevealed;
    return `
      <section class="hnq-note hnq-note-v41 ${revealed ? 'is-revealed' : 'is-locked'} ${this.noteJustRevealed ? 'is-magic-reveal' : ''}">
        <div class="hnq-note__content">
          <p class="hnq-note__label">A note for you</p>
          <div class="hnq-note__body"><p>${this.escape(body)}</p></div>
        </div>
        ${revealed ? '' : `
          <div class="hnq-note__veil" aria-hidden="true"></div>
          <div class="hnq-note__cta-wrap">
            <button class="hnq-note__button" type="button" data-action="toggle-note">
              <span class="hnq-note__button-main">Unveil your private note</span>
              <span class="hnq-note__button-sub">Click to reveal it</span>
            </button>
          </div>
        `}
      </section>
    `;
  }


  renderAnswer(question, index) {
    const chosenIndex = this.answers[index];
    const isCorrect = chosenIndex === question.correct;
    const chosenText = question.options?.[chosenIndex] || 'No answer selected';
    const correctText = question.options?.[question.correct] || '';

    return `
      <article class="hnq-answer ${isCorrect ? 'is-correct' : 'is-wrong'}">
        <div class="hnq-answer__head">
          <h3>${index + 1}. ${this.escape(question.q)}</h3>
          <span class="hnq-answer__chip">${isCorrect ? '✓ Correct' : '✦ Review this one'}</span>
        </div>
        <div class="hnq-answer__grid">
          <div class="hnq-answer__box ${isCorrect ? 'is-good' : 'is-review'}">
            <span>✍ Your answer</span>
            <strong>${this.escape(chosenText)}</strong>
          </div>
          <div class="hnq-answer__box is-good">
            <span>✓ Correct answer</span>
            <strong>${this.escape(correctText)}</strong>
          </div>
        </div>
        ${question.context ? `<div class="hnq-answer__context"><strong>✦ Extra note</strong><p>${this.escape(question.context)}</p></div>` : ''}
      </article>
    `;
  }

  renderProducts() {
    const products = this.productList();
    if (!products.length) return '';
    const copy = this.getRecommendationCopy();

    return `
      <section class="hnq-materials hnq-materials-v10">
        <div class="hnq-materials__intro">
          <p class="hnq-eyebrow">${this.escape(copy.eyebrow)}</p>
          <h3>${this.escape(copy.headline)}</h3>
          <p>${this.escape(copy.body)}</p>
        </div>
        <div class="hnq-product-grid hnq-product-grid--simple">
          ${products.map((product, index) => this.renderProductCard(product, index === 0, copy)).join('')}
        </div>
      </section>
    `;
  }

  renderProductCard(product, primary = false, copy = {}) {
    const fallbackTitle = product.title || 'Hearts Notes resource';
    const mainTitleHtml = product.customShortNameHtml || product.shortTitleHtml || (product.customShortNameText ? this.escape(product.customShortNameText) : this.escape(fallbackTitle));
    const plainTitle = product.customShortNameText || product.shortTitleText || product.shortTitle || fallbackTitle;
    const subtitle = product.title && product.title !== plainTitle ? product.title : '';

    return `
      <a class="hnq-product-card ${primary ? 'hnq-product-card--primary' : ''}" href="${this.escape(product.url || '#')}">
        <div class="hnq-product-card__media">
          ${product.image ? `<img src="${this.escape(product.image)}" alt="${this.escape(plainTitle)}">` : '<div class="hnq-product-card__placeholder"></div>'}
          ${product.isDigital ? '<span class="hnq-product-card__badge">Digital PDF</span>' : ''}
        </div>
        <div class="hnq-product-card__body">
          <div class="hnq-product-card__title">${mainTitleHtml}</div>
          ${subtitle ? `<p>${this.escape(subtitle)}</p>` : ''}
          <span class="hnq-product-card__link">${this.escape(primary ? (copy.primaryLinkLabel || 'Discover this material') : (copy.linkedLinkLabel || 'See more'))}</span>
        </div>
      </a>
    `;
  }

}

if (!customElements.get('hn-quiz')) {
  customElements.define('hn-quiz', HeartsNotesQuiz);
}
